// ui/tooltip.js
// Fast, styled tooltip system - handles both data-tooltip and title attributes

import { getItem, getItemSellPrice, getItemIcon } from '../data/items/index.js';
import { formatNumber } from '../utils/format.js';

let tooltipEl = null;
let showTimeout = null;
let currentTarget = null;
let storedTitle = null; // Store title to prevent native tooltip

const SHOW_DELAY = 150; // ms before showing tooltip

/**
 * Initialize the tooltip system
 * Call this once on page load
 */
export function initTooltips() {
    // Create tooltip element
    tooltipEl = document.createElement('div');
    tooltipEl.className = 'game-tooltip';
    tooltipEl.style.display = 'none';
    document.body.appendChild(tooltipEl);

    // Use event delegation on document
    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('mouseout', handleMouseOut, true);
    document.addEventListener('mousemove', handleMouseMove);
}

/**
 * Get tooltip text from element (data-tooltip takes priority over title)
 */
function getTooltipText(element) {
    if (!element) return null;
    return element.dataset?.tooltip || element.getAttribute('title');
}

/**
 * Find closest element with tooltip
 */
function findTooltipTarget(element) {
    if (!element || !element.closest) return null;
    return element.closest('[data-tooltip], [title]');
}

/**
 * Handle mouseover - start tooltip timer
 */
function handleMouseOver(e) {
    const target = findTooltipTarget(e.target);

    // Clean up orphaned reference if previous target was removed from DOM
    if (currentTarget && !document.contains(currentTarget)) {
        hideTooltip();
    }

    // If we're over a new target, hide previous and show new
    if (target !== currentTarget) {
        hideTooltip();
    }

    if (!target) return;

    const text = getTooltipText(target);
    if (!text) return;

    // If element has title, temporarily remove it to prevent native tooltip
    if (target.hasAttribute('title') && !target.dataset.tooltip) {
        storedTitle = target.getAttribute('title');
        target.removeAttribute('title');
    }

    currentTarget = target;

    // Show after delay
    showTimeout = setTimeout(() => {
        showTooltip(target, text, e);
    }, SHOW_DELAY);
}

/**
 * Handle mouseout - hide tooltip when leaving the target element
 */
function handleMouseOut(e) {
    if (!currentTarget) return;

    // If current target was removed from DOM, clean up immediately
    if (!document.contains(currentTarget)) {
        hideTooltip();
        return;
    }

    const relatedTarget = e.relatedTarget;

    // Check if we're still inside the current target element
    if (relatedTarget && currentTarget.contains(relatedTarget)) {
        return;
    }

    // Check if we're moving to a child of the target
    if (relatedTarget && e.target.contains(relatedTarget)) {
        return;
    }

    // We've left the target - hide tooltip
    hideTooltip();
}

/**
 * Handle mousemove - update position and check if still over target
 */
function handleMouseMove(e) {
    if (!tooltipEl || tooltipEl.style.display === 'none') return;

    // Safety check - if current target was removed from DOM, hide tooltip
    if (currentTarget && !document.contains(currentTarget)) {
        hideTooltip();
        return;
    }

    positionTooltip(e);
}

/**
 * Build tooltip content with item value if applicable
 */
function buildTooltipContent(target, text) {
    // Check if this is an item element with data-item-id
    const itemId = target.dataset?.itemId;
    if (!itemId) return text;

    const item = getItem(itemId);
    if (!item) return text;

    const value = getItemSellPrice(itemId);
    if (value <= 0) return text; // Currency or unsellable items

    // Get quantity from the slot element
    // Try multiple class names (inventory uses item-quantity, bank uses bank-item-quantity)
    let quantity = 1;
    const qtyEl = target.querySelector('.item-quantity, .bank-item-quantity');
    if (qtyEl) {
        // Parse quantity (handles formats like "1.2K", "5.5M", etc.)
        const qtyText = qtyEl.textContent.trim();
        if (qtyText.includes('K')) {
            quantity = parseFloat(qtyText) * 1000;
        } else if (qtyText.includes('M')) {
            quantity = parseFloat(qtyText) * 1000000;
        } else if (qtyText.includes('B')) {
            quantity = parseFloat(qtyText) * 1000000000;
        } else {
            quantity = parseInt(qtyText.replace(/,/g, ''), 10) || 1;
        }
    }

    const stackValue = value * quantity;

    // Build enhanced tooltip with value info
    // Format: "X [coin icon]" for single item, "X [coin icon] (Stack: Y [coin icon])" for stacks
    // Pass gold amounts so quantity-based coin sprites show the right tier
    const coinIcon = getItemIcon('coins', value);
    let valueInfo = `\n<span class="tooltip-value">${formatNumber(value)} ${coinIcon}`;
    if (quantity > 1) {
        const stackCoinIcon = getItemIcon('coins', stackValue);
        valueInfo += ` <span class="tooltip-stack">(Stack: ${formatNumber(stackValue)} ${stackCoinIcon})</span>`;
    }
    valueInfo += '</span>';

    return text + valueInfo;
}

/**
 * Show tooltip for target
 */
function showTooltip(target, text, e) {
    if (!text || target !== currentTarget) return;

    const enhancedText = buildTooltipContent(target, text);
    // Convert newlines to <br> for HTML rendering
    tooltipEl.innerHTML = enhancedText.replace(/\n/g, '<br>');
    tooltipEl.style.display = 'block';
    positionTooltip(e);
}

/**
 * Hide tooltip
 */
function hideTooltip() {
    if (showTimeout) {
        clearTimeout(showTimeout);
        showTimeout = null;
    }

    // Restore title attribute if we removed it (only if element is still in DOM)
    if (currentTarget && storedTitle && document.contains(currentTarget)) {
        currentTarget.setAttribute('title', storedTitle);
    }
    storedTitle = null;
    currentTarget = null;

    if (tooltipEl) {
        tooltipEl.style.display = 'none';
    }
}

/**
 * Position tooltip near cursor, avoiding screen edges
 */
function positionTooltip(e) {
    const padding = 12;
    const offsetX = 15;
    const offsetY = 15;

    let x = e.clientX + offsetX;
    let y = e.clientY + offsetY;

    // Get tooltip dimensions
    const rect = tooltipEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust if going off right edge
    if (x + rect.width + padding > viewportWidth) {
        x = e.clientX - rect.width - offsetX;
    }

    // Adjust if going off bottom edge
    if (y + rect.height + padding > viewportHeight) {
        y = e.clientY - rect.height - offsetY;
    }

    // Ensure not off left/top
    x = Math.max(padding, x);
    y = Math.max(padding, y);

    tooltipEl.style.left = `${x}px`;
    tooltipEl.style.top = `${y}px`;
}

/**
 * Programmatically update tooltip content for an element
 */
export function setTooltip(element, text) {
    if (element) {
        element.dataset.tooltip = text;
        // Update if currently showing this element's tooltip
        if (currentTarget === element && tooltipEl.style.display !== 'none') {
            tooltipEl.innerHTML = text.replace(/\n/g, '<br>');
        }
    }
}

/**
 * Remove tooltip from an element
 */
export function removeTooltip(element) {
    if (element) {
        delete element.dataset.tooltip;
        if (currentTarget === element) {
            hideTooltip();
        }
    }
}

export default { initTooltips, setTooltip, removeTooltip };
